/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { globalRef } from '@amazon/vinyl-util'
import { jsx } from '@amazon/vinyl-tsx'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { getString } from '@/transport/i18n/bundle.generated'
import { globalData } from '@amzn/bf-playback'

/**
 * Default auto-dismiss window for the shared transport tooltip. Long
 * enough to read a short line comfortably, short enough that the surface
 * doesn't become sticky chrome that has to be manually cleared.
 */
const DEFAULT_DURATION_MS = 4500

/** A button on the tooltip; pressing it runs `onPress`, then dismisses the tooltip. */
export interface TransportTooltipAction {
    readonly label: ObservableValue<string>
    readonly primary?: boolean
    readonly onPress?: (event: MouseEvent) => void
}

/** A tooltip with buttons: an optional bold title over the message, then the buttons. */
export interface TransportTooltipWithActions {
    readonly title?: string
    readonly message: string
    readonly actions: readonly TransportTooltipAction[]
}

const title$ = globalData(() => data<string | null>(null))
const message$ = globalData(() => data<string | null>(null))
const actions$ = globalData(() => data<readonly TransportTooltipAction[]>([]))
const tooltipTimerRef = globalRef<{
    timer: ReturnType<typeof setTimeout> | null
    shown: number
    dispose(): void
}>(() => ({
    timer: null,
    shown: 0,
    dispose() {
        if (this.timer != null) clearTimeout(this.timer)
    },
}))

/**
 * Observable read-only view of the current tooltip copy. Null means the
 * host renders nothing. Exported for consumers (e.g. tests, alternate
 * surfaces) that need to react to the tooltip state without owning the
 * timer discipline.
 */
export const transportTooltipMessage$: ObservableValue<string | null> = message$

/**
 * Show a transient tooltip anchored to the transport bar. If a tooltip
 * is already visible its copy is replaced and the timer resets — so a
 * newer signal (e.g. skip-limit fired mid-autoplay tooltip) always wins
 * without stacking. Every transport notice routes through here or
 * `showTransportTooltipWithActions` so the DOM footprint stays a single
 * positioned surface.
 */
export function showTransportTooltip(message: string, durationMs = DEFAULT_DURATION_MS): void {
    show({ message, actions: [] })
    const tooltip = tooltipTimerRef.value
    tooltip.timer = setTimeout(() => {
        message$.value = null
        tooltip.timer = null
    }, durationMs)
}

/**
 * Show a tooltip with buttons. It has no timer: it stays until the customer presses a button or
 * the close icon, a newer tooltip replaces it, or the returned release runs. Release hides this
 * tooltip only while it is still the one showing.
 */
export function showTransportTooltipWithActions(tooltip: TransportTooltipWithActions): () => void {
    const shown = show(tooltip)
    return () => {
        if (tooltipTimerRef.value.shown === shown && message$.value != null) {
            dismissTransportTooltip()
        }
    }
}

function show({ title, message, actions }: TransportTooltipWithActions): number {
    const tooltip = tooltipTimerRef.value
    if (tooltip.timer != null) clearTimeout(tooltip.timer)
    tooltip.timer = null
    title$.value = title ?? null
    actions$.value = actions
    message$.value = message
    return ++tooltip.shown
}

/**
 * Force-dismiss the tooltip. Used by the host's close affordance so the
 * user can hide the surface early. Idempotent — a no-op when nothing is
 * currently showing.
 */
export function dismissTransportTooltip(): void {
    const tooltip = tooltipTimerRef.value
    if (tooltip.timer != null) {
        clearTimeout(tooltip.timer)
        tooltip.timer = null
    }
    message$.value = null
    title$.value = null
    actions$.value = []
}

/**
 * The one positioned surface both tooltip dialogs render into. Mounted
 * as a child of `.transport` so it inherits the transport's containing
 * block — the transport applies `transform` on the swipe path, which
 * would trap a `position: fixed` descendant anyway, so absolute
 * positioning off the transport is the natural fit and keeps the
 * surface visually anchored to the transport bar's top edge.
 */
export function TransportTooltipHost() {
    const visible = message$.map((m) => m != null)
    const text = message$.map((m) => m ?? '')
    return (
        <div
            className="transportTooltip"
            role="status"
            aria-live="polite"
            visible={visible}
            onConnect={(el) => {
                // `inert` alone drops the hidden tooltip from the a11y tree and
                // blurs its dismiss button — a bare aria-hidden can't blur, so it
                // trips the browser's focused-descendant warning on hide.
                return visible.onData((v) => {
                    if (v) el.removeAttribute('inert')
                    else el.setAttribute('inert', '')
                })
            }}
        >
            <div className="transportTooltipText">
                <strong className="transportTooltipTitle" visible={title$.map((t) => t != null)}>
                    {title$.map((t) => t ?? '')}
                </strong>
                <span>{text}</span>
            </div>
            <button
                type="button"
                className="transportTooltipDismiss button"
                aria-label={getString('dmusic_transport_dismiss_accessibility_label')}
                title={getString('dmusic_transport_dismiss_accessibility_label')}
                onclick={dismissTransportTooltip}
            >
                <Icon icon="close" />
            </button>
            <div
                className="transportTooltipActions"
                onConnect={(el) =>
                    actions$.onData((actions) =>
                        el.replaceChildren(
                            ...actions.map((action) => <TooltipActionButton action={action} />)
                        )
                    )
                }
            />
        </div>
    )
}

function TooltipActionButton(props: { readonly action: TransportTooltipAction }) {
    const { action } = props
    return (
        <Button
            type="button"
            className={action.primary ? 'button textButton' : 'button textButton secondary'}
            onclick={(event) => {
                action.onPress?.(event)
                dismissTransportTooltip()
            }}
        >
            {action.label}
        </Button>
    )
}
